import {
  Fragment,
  useEffect,
  useId,
  useMemo,
  useState,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  CUSTOMER_GENDERS,
  CUSTOMER_KINDS,
  CUSTOMER_MEDIA_ENTITY_TYPE,
  CUSTOMER_SOURCES,
  DEFAULT_CUSTOMER_KIND,
  INDIAN_STATES,
  createCustomerSchema,
  updateCustomerSchema,
  type CapturedMeasurementsInput,
  type CreateCustomerInput,
  type Customer,
  type CustomerGender,
  type CustomerKind,
  type DuplicatePhoneDetails,
  type ResolvedCustomField,
} from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import { useFormFields } from '../../lib/settings/use-shop-settings.js';
import { useResolvedCustomFields } from '../../lib/custom-fields/use-custom-fields.js';
import { customValuesPayload, customValuesToFormState } from '../../lib/custom-fields/values.js';
import { duplicatePhoneOf } from '../../lib/customers/customers-api.js';
import { CustomValueInput } from '../custom-fields/custom-value-input.js';
import { CustomerTagsInput } from './customer-tags-input.js';
import { StaffAssigneeSelect } from './staff-assignee-select.js';
import { MediaUploader } from '../media/media-uploader.js';
import { useCreateCustomer, useUpdateCustomer } from '../../lib/customers/use-customers.js';
import { formatDate, formatMobile } from '../../lib/format.js';
import { Button, Input, Textarea, Label, useToast, cn } from '@tailoring-crm/ui';

/**
 * Adding or editing a customer — the one form, in two shells.
 *
 * `variant="page"` is the full record: sections with headings, the shop's running number and
 * the date it was registered at the top, and a footer that can save straight into a booking.
 * `variant="dialog"` is the same fields in one plain column, for the counter adding a walk-in
 * without leaving the booking form it is halfway through. They share this component rather than
 * being two forms, because everything below the layout — validation, the duplicate answer, the
 * shop's own field order, the custom fields — has to behave identically or a customer added at
 * the counter would be a different kind of record from one added on the page.
 *
 * Validation is the SHARED schema, run here on submit rather than through a resolver. That is
 * not a shortcut — it is what guarantees the browser rejects exactly what the endpoint would,
 * including the mobile-number normalisation, so a number the form accepted can never be
 * refused by the server for a reason the form could have explained.
 *
 * The one interaction worth reading carefully is the duplicate. A 409 is not a failure here:
 * the shop asked "does this person exist?" and the answer was yes. So the form turns it into
 * the two choices the counter actually has — open the customer it found, or insist, because
 * a mother really is booking on the same handset as her daughter.
 */

export type CustomerFormVariant = 'page' | 'dialog';

/** What the person pressed. `save-and-order` walks straight on to booking. */
export type CustomerSaveIntent = 'save' | 'save-and-order';

interface FormValues {
  firstName: string;
  lastName: string;
  primaryPhone: string;
  altPhone: string;
  whatsappPhone: string;
  gender: string;
  profession: string;
  preferredStyle: string;
  kind: CustomerKind;
  gstin: string;
  dateOfBirth: string;
  anniversary: string;
  email: string;
  notes: string;
  source: string;
  /** The staff member looking after them, or `''` for nobody. */
  assignedToUserId: string;
  line1: string;
  city: string;
  stateCode: string;
  pincode: string;
}

const EMPTY: FormValues = {
  firstName: '',
  lastName: '',
  primaryPhone: '',
  altPhone: '',
  whatsappPhone: '',
  gender: '',
  profession: '',
  preferredStyle: '',
  kind: DEFAULT_CUSTOMER_KIND,
  gstin: '',
  dateOfBirth: '',
  anniversary: '',
  email: '',
  notes: '',
  source: '',
  assignedToUserId: '',
  line1: '',
  city: '',
  stateCode: '',
  pincode: '',
};

function valuesFrom(customer: Customer): FormValues {
  return {
    firstName: customer.firstName || customer.name,
    lastName: customer.lastName ?? '',
    primaryPhone: customer.primaryPhone,
    altPhone: customer.altPhone ?? '',
    whatsappPhone: customer.whatsappPhone ?? '',
    gender: customer.gender ?? '',
    profession: customer.profession ?? '',
    preferredStyle: customer.preferredStyle ?? '',
    kind: customer.kind,
    gstin: customer.gstin ?? '',
    dateOfBirth: customer.dateOfBirth ?? '',
    anniversary: customer.anniversary ?? '',
    email: customer.email ?? '',
    notes: customer.notes ?? '',
    source: customer.source ?? '',
    assignedToUserId: customer.assignedToUserId ?? '',
    line1: customer.address.line1 ?? '',
    city: customer.address.city ?? '',
    stateCode: customer.address.stateCode ?? '',
    pincode: customer.address.pincode ?? '',
  };
}

/** Blank strings mean "not given", never an empty value the API should store. */
function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** The whole record as the API takes it. Tags always go up, so removing the last one clears them. */
function payloadFrom(
  values: FormValues,
  tags: readonly string[],
  photoMediaId: string | null,
  customValues: Record<string, string>,
  customFields: readonly ResolvedCustomField[],
): Record<string, unknown> {
  return {
    // The halves, not the whole. The API joins them and keeps `name` in step — see
    // `resolveCustomerName`, and `splitCustomerName` for why both spellings are stored.
    firstName: values.firstName,
    lastName: blankToNull(values.lastName),
    primaryPhone: values.primaryPhone,
    altPhone: blankToNull(values.altPhone),
    whatsappPhone: blankToNull(values.whatsappPhone),
    gender: blankToNull(values.gender),
    profession: blankToNull(values.profession),
    preferredStyle: blankToNull(values.preferredStyle),
    kind: values.kind,
    // A GSTIN typed against a business and then switched back to a consumer is not sent — the
    // API would refuse the pair, and refusing a form over a field the counter can no longer
    // see would be unexplainable. Switching the type is how you clear it.
    gstin: values.kind === 'b2b' ? blankToNull(values.gstin) : null,
    dateOfBirth: blankToNull(values.dateOfBirth),
    anniversary: blankToNull(values.anniversary),
    email: blankToNull(values.email),
    notes: blankToNull(values.notes),
    source: blankToNull(values.source),
    assignedToUserId: blankToNull(values.assignedToUserId),
    tags: [...tags],
    photoMediaId,
    address: {
      line1: blankToNull(values.line1),
      line2: null,
      city: blankToNull(values.city),
      stateCode: blankToNull(values.stateCode),
      pincode: blankToNull(values.pincode),
    },
    // The shop's own fields, in their typed slots — see `custom-fields.ts`.
    custom: customValuesPayload(customValues, customFields),
  };
}

/**
 * Which section each field belongs to on the page.
 *
 * A LOOKUP RATHER THAN THREE LISTS, so the shop's stored field order still decides what comes
 * first inside a section: the layout is walked once, in the shop's order, and each key is
 * dropped into the section it names. A key this map does not mention falls into the last
 * section, which is what a custom field added later should do rather than disappear.
 */
const SECTION_OF: Readonly<Record<string, CustomerFormSection>> = Object.freeze({
  name: 'details',
  profession: 'details',
  gender: 'details',
  preferredStyle: 'details',
  kind: 'details',
  gstin: 'details',
  primaryPhone: 'contact',
  whatsappPhone: 'contact',
  altPhone: 'contact',
  email: 'contact',
  address: 'contact',
  dateOfBirth: 'dates',
  anniversary: 'dates',
});

/** The cards the page draws, in the order it draws them. */
type CustomerFormSection = 'details' | 'contact' | 'dates' | 'more';

/**
 * The blocks that take a whole row of a section's two-column grid.
 *
 * Two reasons a field is here, and they are different. `name` and `address` are grids of their
 * own — putting a two-box group inside one column of a two-column grid would give the surname a
 * quarter of the width. `preferredStyle` and `notes` are single fields that are simply LONG: a
 * line a tailor reads at a glance, and a paragraph, neither of which is legible in half a card.
 */
const FULL_WIDTH_BLOCKS: ReadonlySet<string> = new Set([
  'name',
  'address',
  'preferredStyle',
  'notes',
]);

export interface CustomerFormProps {
  variant?: CustomerFormVariant;
  /** Present for an edit; absent for a create. */
  customer?: Customer | null;
  /**
   * What the counter had already typed when it decided to create. A digits-only search becomes
   * the mobile number, anything else becomes the name — so the form opens half-filled and the
   * owner types the other half.
   */
  prefillQuery?: string;
  onSaved?: (customer: Customer, intent: CustomerSaveIntent) => void;
  /** The counter chose to open the customer the duplicate check found instead. */
  onOpenExisting?: (customerId: string) => void;
  onCancel?: () => void;
  /**
   * Measurements taken at the counter while this customer was being added, held by whoever
   * renders the form and posted WITH her — the API writes both in one transaction. Meaningful
   * on a create only: an existing customer's measurements are saved against her directly, from
   * the panel on her own page, and do not belong in a form about her address.
   */
  measurements?: readonly CapturedMeasurementsInput[];
  /** Rendered between the fields and the footer. The page hangs its gallery and history here. */
  children?: ReactNode;
}

export function CustomerForm({
  variant = 'dialog',
  customer = null,
  prefillQuery = '',
  onSaved,
  onOpenExisting,
  onCancel,
  measurements = [],
  children,
}: CustomerFormProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fieldId = useId();

  const isEdit = customer !== null;
  const isPage = variant === 'page';
  const create = useCreateCustomer();
  const update = useUpdateCustomer(customer?.id ?? '');
  const { fields: configuredFields } = useResolvedCustomFields('customers');

  /** The fields this form asks for beyond the built-ins: the shop's own, minus anything hidden. */
  const customFields = useMemo(
    () =>
      configuredFields.filter(
        (field) => field.origin === 'custom' && !field.hidden && field.type !== null,
      ),
    [configuredFields],
  );

  const initialValues = useMemo<FormValues>(() => {
    if (customer) return valuesFrom(customer);
    const typed = prefillQuery.trim();
    if (!typed) return EMPTY;
    return /^[\d+\-\s()]+$/.test(typed)
      ? { ...EMPTY, primaryPhone: typed, whatsappPhone: typed }
      : { ...EMPTY, firstName: typed };
  }, [customer, prefillQuery]);

  const initialCustomValues = useMemo<Record<string, string>>(
    () => (customer ? customValuesToFormState(customer.custom ?? [], configuredFields) : {}),
    [customer, configuredFields],
  );

  const [values, setValues] = useState<FormValues>(initialValues);
  const [customValues, setCustomValues] = useState<Record<string, string>>(initialCustomValues);
  const [photoMediaId, setPhotoMediaId] = useState<string | null>(customer?.photoMediaId ?? null);
  const [tags, setTags] = useState<string[]>(customer?.tags ?? []);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicatePhoneDetails | null>(null);

  // Reopening the form for a different customer must not show the previous one's details.
  useEffect(() => {
    setValues(initialValues);
    setCustomValues(initialCustomValues);
    setPhotoMediaId(customer?.photoMediaId ?? null);
    setTags(customer?.tags ?? []);
    setFieldErrors({});
    setFormError(null);
    setDuplicate(null);
  }, [initialValues, initialCustomValues, customer]);

  /**
   * `kind` is the one field that is not a free string, and switching it to `b2c` clears the
   * GSTIN box as well — leaving a stale tax number invisible behind a hidden field is how it
   * would end up on a bill nobody meant to put it on.
   */
  const setKind = (kind: CustomerKind): void => {
    setValues((previous) => ({
      ...previous,
      kind,
      gstin: kind === 'b2b' ? previous.gstin : '',
    }));
    setFieldErrors((previous) => {
      if (!('gstin' in previous)) return previous;
      const next = { ...previous };
      delete next.gstin;
      return next;
    });
  };

  const set = (field: Exclude<keyof FormValues, 'kind'>, value: string): void => {
    setValues((previous) => {
      const next = { ...previous, [field]: value };
      // The WhatsApp number follows the mobile number until somebody types a different one.
      // It is the same handset for almost every walk-in, and a required field that fills itself
      // in costs the counter nothing — while one that had to be retyped would be typed wrong.
      if (field === 'primaryPhone' && previous.whatsappPhone === previous.primaryPhone) {
        next.whatsappPhone = value;
      }
      return next;
    });
    setFieldErrors((previous) => {
      if (!(field in previous)) return previous;
      const next = { ...previous };
      delete next[field];
      return next;
    });
    // Editing the number is how the counter answers "no, this is somebody else".
    if (field === 'primaryPhone') setDuplicate(null);
  };

  const setCustomValue = (key: string, value: string): void => {
    setCustomValues((previous) => ({ ...previous, [key]: value }));
    setFieldErrors((previous) => {
      const errorKey = `custom.${key}`;
      if (!(errorKey in previous)) return previous;
      const next = { ...previous };
      delete next[errorKey];
      return next;
    });
  };

  const clear = (): void => {
    setValues(customer ? valuesFrom(customer) : EMPTY);
    setCustomValues(initialCustomValues);
    setTags(customer?.tags ?? []);
    setFieldErrors({});
    setFormError(null);
    setDuplicate(null);
  };

  const submit = async (
    allowDuplicatePhone: boolean,
    intent: CustomerSaveIntent = 'save',
  ): Promise<void> => {
    setFieldErrors({});
    setFormError(null);

    const payload = {
      ...payloadFrom(values, tags, photoMediaId, customValues, customFields),
      ...(allowDuplicatePhone ? { allowDuplicatePhone } : {}),
      // Absent rather than empty when there are none: the API treats an absent key as "this
      // caller does not measure", which is every existing one, and an empty array would make
      // the create route ask for a permission the counter did not need a moment ago.
      ...(!isEdit && measurements.length > 0 ? { measurements: [...measurements] } : {}),
    };
    const schema = isEdit ? updateCustomerSchema : createCustomerSchema;
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        // The address is one visual group; a message about `address.pincode` belongs on the
        // PIN code input, which the form knows as `pincode`.
        // Likewise one message about `tags.3` belongs on the one tags box.
        const path = issue.path
          .join('.')
          .replace(/^address\./, '')
          .replace(/^tags\..*$/, 'tags');
        next[path] ??= issue.message;
      }
      // The API reports a missing name against `name`, which is not a box on this form. Move it
      // onto the box the person can actually do something about.
      if (next.name && !next.firstName) next.firstName = next.name;
      // While the WhatsApp box is still following the mobile box, it does not repeat its
      // complaint: one half-typed number is one mistake, and reporting it twice sends the
      // counter looking for a second thing to fix that is not there.
      if (
        values.whatsappPhone === values.primaryPhone &&
        next.whatsappPhone === next.primaryPhone
      ) {
        delete next.whatsappPhone;
      }
      setFieldErrors(next);
      return;
    }

    try {
      const saved = isEdit
        ? await update.mutateAsync(parsed.data)
        : await create.mutateAsync(parsed.data as CreateCustomerInput);
      toast({
        title: isEdit ? t('customers.saved') : t('customers.created', { name: saved.name }),
        variant: 'success',
      });
      onSaved?.(saved, intent);
    } catch (error) {
      const found = duplicatePhoneOf(error);
      if (found) {
        setDuplicate(found);
        return;
      }
      if (isApiRequestError(error) && error.fieldErrors) {
        const next: Record<string, string> = {};
        for (const [path, messages] of Object.entries(error.fieldErrors)) {
          next[path.replace(/^address\./, '')] = messages.join(' ');
        }
        setFieldErrors(next);
        setFormError(Object.keys(next).length > 0 ? null : error.message);
        return;
      }
      setFormError(error instanceof Error ? error.message : t('error.unexpected'));
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void submit(false);
  };

  const pending = create.isPending || update.isPending;

  const errorFor = (field: string): ReactElement | null =>
    fieldErrors[field] ? (
      <p
        id={`${fieldId}-${field}-error`}
        role="alert"
        className="text-sm font-medium text-destructive"
      >
        {fieldErrors[field]}
      </p>
    ) : null;

  const describedBy = (field: string): string | undefined =>
    fieldErrors[field] ? `${fieldId}-${field}-error` : undefined;

  /**
   * A ten-digit box with the country code fixed beside it.
   *
   * The `+91` is a label, not an input. Every number this product accepts is Indian —
   * `indianMobileSchema` is what the sign-in, the reminder and the duplicate check all agree
   * on — so a picker offering 246 other codes would be offering a choice that cannot be saved.
   */
  const phoneBlock = (
    field: 'primaryPhone' | 'altPhone' | 'whatsappPhone',
    label: string,
    options: { required?: boolean; autoComplete?: string } = {},
  ): ReactElement => (
    <div className="flex flex-col gap-1.5">
      <LabelRow htmlFor={`${fieldId}-${field}`} label={label} required={options.required} />
      <div className="flex items-stretch">
        <span
          aria-hidden="true"
          className="flex items-center rounded-l-md border border-r-0 border-input bg-muted px-3 text-sm text-muted-foreground"
        >
          +91
        </span>
        <Input
          id={`${fieldId}-${field}`}
          // `tel` puts the numeric keypad under the thumb, which is the whole difference
          // between a fast booking and a slow one.
          type="tel"
          inputMode="numeric"
          maxLength={10}
          value={values[field]}
          onChange={(event) => set(field, event.target.value)}
          placeholder={t('customers.field.primaryPhonePlaceholder')}
          autoComplete={options.autoComplete ?? 'off'}
          className="rounded-l-none"
          aria-invalid={fieldErrors[field] ? true : undefined}
          aria-describedby={describedBy(field)}
        />
      </div>
      {errorFor(field)}
    </div>
  );

  /** Which fields this shop asks for, and in what order. Resolved, so never empty. */
  const layout = useFormFields('customer');

  /**
   * The form's fields, keyed, so the SHOP's layout decides which are asked for and in what
   * order. Built as a map rather than rendered inline because order is half of what a layout
   * is: hiding a field would have been a condition on each block, but moving one requires the
   * blocks to be addressable.
   *
   * `name` and `primaryPhone` are in here like everything else — `resolveFormFields` puts them
   * back whatever the stored layout says, because the API refuses a customer without them and a
   * form that could hide either is a form that cannot be submitted.
   */
  const blocks: Record<string, ReactNode> = {
    /* One field, two boxes. The pair is a single layout entry because the API stores them as a
       single name — a shop that could hide the surname alone would have hidden half a field. */
    name: (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <LabelRow
            htmlFor={`${fieldId}-firstName`}
            label={t('customers.field.firstName')}
            required
          />
          <Input
            id={`${fieldId}-firstName`}
            value={values.firstName}
            onChange={(event) => set('firstName', event.target.value)}
            placeholder={t('customers.field.firstNamePlaceholder')}
            autoComplete="given-name"
            aria-invalid={fieldErrors.firstName ? true : undefined}
            aria-describedby={describedBy('firstName')}
          />
          {errorFor('firstName')}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-lastName`}>{t('customers.field.lastName')}</Label>
          <Input
            id={`${fieldId}-lastName`}
            value={values.lastName}
            onChange={(event) => set('lastName', event.target.value)}
            placeholder={t('customers.field.lastNamePlaceholder')}
            autoComplete="family-name"
            aria-invalid={fieldErrors.lastName ? true : undefined}
            aria-describedby={describedBy('lastName')}
          />
          {errorFor('lastName')}
        </div>
      </div>
    ),

    profession: (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${fieldId}-profession`}>{t('customers.field.profession')}</Label>
        <Input
          id={`${fieldId}-profession`}
          value={values.profession}
          onChange={(event) => set('profession', event.target.value)}
          placeholder={t('customers.field.professionPlaceholder')}
          aria-invalid={fieldErrors.profession ? true : undefined}
          aria-describedby={describedBy('profession')}
        />
        {errorFor('profession')}
      </div>
    ),

    gender: (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${fieldId}-gender`}>{t('customers.field.gender')}</Label>
        <select
          id={`${fieldId}-gender`}
          value={values.gender}
          onChange={(event) => set('gender', event.target.value as CustomerGender | '')}
          className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
        >
          <option value="">{t('customers.gender.none')}</option>
          {CUSTOMER_GENDERS.map((gender) => (
            <option key={gender} value={gender}>
              {t(`customers.gender.${gender}`)}
            </option>
          ))}
        </select>
      </div>
    ),

    preferredStyle: (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${fieldId}-preferredStyle`}>{t('customers.field.preferredStyle')}</Label>
        <Input
          id={`${fieldId}-preferredStyle`}
          value={values.preferredStyle}
          onChange={(event) => set('preferredStyle', event.target.value)}
          placeholder={t('customers.field.preferredStylePlaceholder')}
          aria-invalid={fieldErrors.preferredStyle ? true : undefined}
          aria-describedby={describedBy('preferredStyle')}
        />
        {errorFor('preferredStyle')}
        <p className="text-xs text-muted-foreground">{t('customers.field.preferredStyleHelp')}</p>
      </div>
    ),

    /* Type decides what the form asks for next: a business is the only customer that can carry
       a tax number, and a shop that never sells to one never sees the second field. */
    kind: (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${fieldId}-kind`}>{t('customers.field.kind')}</Label>
        <select
          id={`${fieldId}-kind`}
          value={values.kind}
          onChange={(event) => setKind(event.target.value as CustomerKind)}
          className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
        >
          {CUSTOMER_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {t(`customers.kind.${kind}`)}
            </option>
          ))}
        </select>
      </div>
    ),

    gstin:
      values.kind === 'b2b' ? (
        <div className="flex flex-col gap-1.5">
          <LabelRow htmlFor={`${fieldId}-gstin`} label={t('customers.field.gstin')} required />
          <Input
            id={`${fieldId}-gstin`}
            value={values.gstin}
            onChange={(event) => set('gstin', event.target.value.toUpperCase())}
            placeholder={t('customers.field.gstinPlaceholder')}
            maxLength={15}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={fieldErrors.gstin ? true : undefined}
            aria-describedby={describedBy('gstin')}
          />
          {errorFor('gstin')}
          <p className="text-xs text-muted-foreground">{t('customers.field.gstinHelp')}</p>
        </div>
      ) : null,

    primaryPhone: phoneBlock('primaryPhone', t('customers.field.primaryPhone'), {
      required: true,
      autoComplete: 'tel',
    }),

    whatsappPhone: phoneBlock('whatsappPhone', t('customers.field.whatsappPhone')),

    altPhone: phoneBlock('altPhone', t('customers.field.altPhone')),

    source: (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${fieldId}-source`}>{t('customers.field.source')}</Label>
        <select
          id={`${fieldId}-source`}
          value={values.source}
          onChange={(event) => set('source', event.target.value)}
          className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
        >
          <option value="">{t('customers.source.none')}</option>
          {CUSTOMER_SOURCES.map((source) => (
            <option key={source} value={source}>
              {t(`customers.source.${source}`)}
            </option>
          ))}
        </select>
      </div>
    ),

    dateOfBirth: (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${fieldId}-dateOfBirth`}>{t('customers.field.dateOfBirth')}</Label>
        <Input
          id={`${fieldId}-dateOfBirth`}
          type="date"
          value={values.dateOfBirth}
          onChange={(event) => set('dateOfBirth', event.target.value)}
          aria-invalid={fieldErrors.dateOfBirth ? true : undefined}
          aria-describedby={describedBy('dateOfBirth')}
        />
        {errorFor('dateOfBirth')}
      </div>
    ),

    anniversary: (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${fieldId}-anniversary`}>{t('customers.field.anniversary')}</Label>
        <Input
          id={`${fieldId}-anniversary`}
          type="date"
          value={values.anniversary}
          onChange={(event) => set('anniversary', event.target.value)}
          aria-invalid={fieldErrors.anniversary ? true : undefined}
          aria-describedby={describedBy('anniversary')}
        />
        {errorFor('anniversary')}
      </div>
    ),

    email: (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${fieldId}-email`}>{t('customers.field.email')}</Label>
        <Input
          id={`${fieldId}-email`}
          type="email"
          value={values.email}
          onChange={(event) => set('email', event.target.value)}
          autoComplete="email"
          aria-invalid={fieldErrors.email ? true : undefined}
          aria-describedby={describedBy('email')}
        />
        {errorFor('email')}
      </div>
    ),

    /* One registry field, four boxes. A shop that does not take addresses turns off the
       address, not the pincode — nobody thinks of those as separate decisions. */
    address: (
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-stateCode`}>{t('customers.field.state')}</Label>
            <select
              id={`${fieldId}-stateCode`}
              value={values.stateCode}
              onChange={(event) => set('stateCode', event.target.value)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              <option value="">{t('customers.field.stateNone')}</option>
              {/* Stored as the GST state code, shown as the name: the code is what an invoice
                  compares to decide IGST against CGST+SGST, and no counter types one. */}
              {INDIAN_STATES.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
            {errorFor('stateCode')}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-city`}>{t('customers.field.city')}</Label>
            <Input
              id={`${fieldId}-city`}
              value={values.city}
              onChange={(event) => set('city', event.target.value)}
              autoComplete="address-level2"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-pincode`}>{t('customers.field.pincode')}</Label>
          <Input
            id={`${fieldId}-pincode`}
            inputMode="numeric"
            maxLength={6}
            value={values.pincode}
            onChange={(event) => set('pincode', event.target.value)}
            autoComplete="postal-code"
            aria-invalid={fieldErrors.pincode ? true : undefined}
            aria-describedby={describedBy('pincode')}
          />
          {errorFor('pincode')}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-line1`}>{t('customers.field.addressLine1')}</Label>
          <Input
            id={`${fieldId}-line1`}
            value={values.line1}
            onChange={(event) => set('line1', event.target.value)}
            autoComplete="address-line1"
          />
        </div>
      </div>
    ),

    notes: (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${fieldId}-notes`}>{t('customers.field.notes')}</Label>
        <Textarea
          id={`${fieldId}-notes`}
          value={values.notes}
          onChange={(event) => set('notes', event.target.value)}
          placeholder={t('customers.field.notesPlaceholder')}
        />
      </div>
    ),
  };

  /**
   * One section's fields on a two-column grid, in the shop's own order.
   *
   * Two columns rather than one because a customer record is mostly SHORT fields — a name, a
   * gender, a PIN code — and a column of them a thousand pixels wide reads as a form far longer
   * than it is. The ones that genuinely need the width say so; see `FULL_WIDTH_BLOCKS`.
   */
  const grid = (keys: readonly string[], extra: ReactNode = null): ReactElement => (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {keys.map((key) =>
        blocks[key] ? (
          <div key={key} className={cn(FULL_WIDTH_BLOCKS.has(key) && 'sm:col-span-2')}>
            {blocks[key]}
          </div>
        ) : null,
      )}
      {extra}
    </div>
  );

  /** The shop's order, split into the sections the page draws. See `SECTION_OF`. */
  const bySection = useMemo(() => {
    const grouped: Record<CustomerFormSection, string[]> = {
      details: [],
      contact: [],
      dates: [],
      more: [],
    };
    for (const key of layout) grouped[SECTION_OF[key] ?? 'more'].push(key);
    return grouped;
  }, [layout]);

  const tagsBlock = (
    <div key="tags" className={cn(isPage && 'sm:col-span-2')}>
      <CustomerTagsInput
        id={`${fieldId}-tags`}
        value={tags}
        onChange={(next) => {
          setTags(next);
          setFieldErrors((previous) => {
            if (!('tags' in previous)) return previous;
            const rest = { ...previous };
            delete rest.tags;
            return rest;
          });
        }}
        error={errorFor('tags')}
        describedBy={describedBy('tags')}
      />
    </div>
  );

  /**
   * Who is looking after this customer. Not one of the shop's configurable fields: it is the
   * handle the follow-ups queue and the customer list's "Assigned to" filter hang off, so every
   * form offers it. Optional — most walk-ins are nobody's in particular.
   */
  const assigneeBlock = (
    <div key="assignedTo" className="flex flex-col gap-1.5">
      <Label htmlFor={`${fieldId}-assignedToUserId`}>{t('customers.field.assignedTo')}</Label>
      <StaffAssigneeSelect
        id={`${fieldId}-assignedToUserId`}
        value={values.assignedToUserId}
        currentName={customer?.assignedToName ?? null}
        onChange={(userId) => set('assignedToUserId', userId)}
        className="h-11 px-3 text-base sm:text-sm"
      />
      {errorFor('assignedToUserId')}
    </div>
  );

  /** The shop's own fields, rendered from their types. */
  const customBlocks = customFields.map((field) => (
    <CustomValueInput
      key={field.key}
      field={field}
      id={`${fieldId}-custom-${field.key}`}
      value={customValues[field.key] ?? ''}
      onChange={(value) => setCustomValue(field.key, value)}
      error={errorFor(`custom.${field.key}`)}
      describedBy={describedBy(`custom.${field.key}`)}
      invalid={Boolean(fieldErrors[`custom.${field.key}`])}
    />
  ));

  const banners = (
    <>
      {formError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
        >
          {formError}
        </p>
      ) : null}

      {/* The duplicate answer, rendered as the choice it is rather than as a failure. */}
      {duplicate ? (
        <div
          role="alert"
          className="flex flex-col gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-3"
        >
          <p className="text-sm font-semibold">{t('customers.duplicate.title')}</p>
          <p className="text-sm text-muted-foreground">
            {t('customers.duplicate.description', {
              name: duplicate.existingCustomerName,
              phone: formatMobile(values.primaryPhone),
            })}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button size="sm" onClick={() => onOpenExisting?.(duplicate.existingCustomerId)}>
              {t('customers.duplicate.openExisting', { name: duplicate.existingCustomerName })}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() => void submit(true)}
            >
              {t('customers.duplicate.addAnyway')}
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );

  /* ---------------------------------------------------------------------------------------
   * The dialog: one plain column, the way the counter has always had it.
   * ------------------------------------------------------------------------------------ */
  if (!isPage) {
    return (
      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        {banners}
        {layout.map((key) => (
          <Fragment key={key}>{blocks[key] ?? null}</Fragment>
        ))}
        {customBlocks}
        {assigneeBlock}
        {tagsBlock}
        {children}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {onCancel ? (
            <Button variant="outline" onClick={onCancel} disabled={pending}>
              {t('common.cancel')}
            </Button>
          ) : null}
          <Button type="submit" disabled={pending}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    );
  }

  /* ---------------------------------------------------------------------------------------
   * The page: the whole record, in sections.
   * ------------------------------------------------------------------------------------ */
  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
      {banners}

      <FormSection
        title={t('customers.section.details')}
        description={t('customers.section.detailsHint')}
      >
        {/* What the shop calls this customer and when they first walked in. Both read-only:
            the number comes from the shop's own sequence and the date is the day the record
            was made, and neither is something a person gets to decide after the fact. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ReadOnlyField
            label={t('customers.field.serialNumber')}
            value={customer?.serialNumber ? String(customer.serialNumber) : '—'}
          />
          <ReadOnlyField
            label={t('customers.field.registeredOn')}
            value={formatDate(customer?.createdAt ?? new Date().toISOString())}
          />
        </div>
        {grid(bySection.details, assigneeBlock)}
      </FormSection>

      <FormSection
        title={t('customers.section.contact')}
        description={t('customers.section.contactHint')}
      >
        {grid(bySection.contact)}
      </FormSection>

      {bySection.dates.length > 0 ? (
        <FormSection
          title={t('customers.section.dates')}
          description={t('customers.section.datesHint')}
        >
          {grid(bySection.dates)}
        </FormSection>
      ) : null}

      {/* Always drawn now: the tags live here even for a shop with no fields of its own. */}
      <FormSection
        title={t('customers.section.more')}
        description={t('customers.section.moreHint')}
      >
        {/* The shop's own fields ride on the same grid, each in a column of its own. */}
        {grid(bySection.more, [...customBlocks, tagsBlock])}
      </FormSection>

      <FormSection
        title={t('customers.section.gallery')}
        description={t('customers.section.galleryHint')}
      >
        <CustomerPhoto
          customerId={customer?.id ?? null}
          // The same gate the counter would apply itself: a photograph filed against a record
          // with no name, no number and no address is a photograph nobody can attach to anybody.
          ready={Boolean(
            values.firstName.trim() && values.primaryPhone.trim() && values.line1.trim(),
          )}
          onChange={setPhotoMediaId}
        />
      </FormSection>

      {children}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {/* Booking is what a new customer is usually here for, so it is one press rather than a
            save followed by a hunt for the order button. */}
        <Button
          variant="outline"
          disabled={pending}
          onClick={() => void submit(false, 'save-and-order')}
        >
          {t('customers.form.saveAndOrder')}
        </Button>
        <Button variant="outline" onClick={clear} disabled={pending}>
          {t('customers.form.clear')}
        </Button>
        <Button type="submit" disabled={pending} data-tour="customer-save">
          {isEdit ? t('customers.form.saveChanges') : t('customers.form.saveCustomer')}
        </Button>
      </div>
    </form>
  );
}

/**
 * A label, with a red asterisk beside it when the API will refuse the form without the field.
 *
 * The asterisk is a SIBLING of the label rather than a child of it, which is not a styling
 * preference: a screen reader announcing the box, and a test asking for it by name, both read
 * the label's text, and "First name*" is not what anybody is looking for. The requirement is
 * announced by the input's own `required`-shaped validation message instead.
 */
function LabelRow({
  htmlFor,
  label,
  required = false,
}: {
  htmlFor: string;
  label: string;
  required?: boolean;
}): ReactElement {
  return (
    <div className="flex items-center gap-0.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {required ? (
        <span aria-hidden="true" className="text-destructive">
          *
        </span>
      ) : null}
    </div>
  );
}

/** One card: a heading and its blurb on the left, the fields on the right. */
function FormSection({
  title,
  description,
  children,
  className,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <section
      className={cn('rounded-lg border border-border bg-card p-4 sm:p-6', className)}
      aria-label={title}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
        <div className="lg:w-64 lg:shrink-0">
          <h3 className="text-base font-semibold leading-tight">{title}</h3>
          {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-4">{children}</div>
      </div>
    </section>
  );
}

/** A value the shop is shown but does not get to type. */
function ReadOnlyField({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      <p className="flex h-11 items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
        {value}
      </p>
    </div>
  );
}

/**
 * The customer's photograph.
 *
 * Gated rather than disabled-with-no-explanation: until the record has a name, a number and an
 * address there is nothing for the picture to belong to, and the uploader says so instead of
 * silently refusing the file somebody just chose.
 */
function CustomerPhoto({
  customerId,
  ready,
  onChange,
}: {
  customerId: string | null;
  ready: boolean;
  onChange: (mediaId: string | null) => void;
}): ReactElement {
  const { t } = useTranslation();
  if (!ready) {
    return <p className="text-sm text-muted-foreground">{t('customers.form.photoBlocked')}</p>;
  }
  return (
    <MediaUploader
      entityType={CUSTOMER_MEDIA_ENTITY_TYPE}
      entityId={customerId}
      maxFiles={1}
      onChange={(media) => onChange(media[0]?.id ?? null)}
    />
  );
}
